/* oxlint-disable better-tailwindcss/no-restricted-classes */
import { Accordion as ArkAccordion } from "@ark-ui/solid";
import type { Component, VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, For, Match, Show, Switch, untrack } from "solid-js";
import { Icon } from "../icon/Icon";
import type { IconButtonMenuProps } from "../listItemMenu/ListItemMenu";
import { IconButtonMenu } from "../listItemMenu/ListItemMenu";
import { IconButton } from "../button/Button";
import { difference, isEmpty, isNil, isString } from "lodash-es";
import { softContains } from "../../utils/stringUtil";
import { createPreviousMemo } from "../../utils/createPreviousMemo";
import Text from "../text/Text";

type AccordionItem = {
  label: string;
  key: string;
  components: AccordionItemComponent[];
  button?: AccordionItemDestructiveButton | AccordionItemDropdownButton;
};

export type AccordionItemComponent = {
  Component: Component;
  filterKey: string | ((query: string) => boolean);
};

type AccordionItemDestructiveButton = {
  type: "destructive";
  "aria-label": string;
  onClick: () => void;
};

type AccordionItemDropdownButton = {
  type: "dropdown";
} & IconButtonMenuProps<string>;

type AccordionProps = {
  initialClosed?: boolean;
  items: AccordionItem[];
  filterQuery?: string;
  EmptyState?: Component;
  variant: "default" | "compact" | "list";
};

export const Accordion: VoidComponent<AccordionProps> = (props) => {
  const filteredComponents = createMemo(() => {
    const query = props.filterQuery;
    return props.items.reduce<Record<string, AccordionItemComponent[]>>((prev, curr) => {
      const components = curr.components.filter((c) =>
        isString(c.filterKey) ? softContains(c.filterKey, query) : c.filterKey(query ?? ""),
      );
      return { ...prev, [curr.key]: components };
    }, {});
  });
  const hasVisibleComponents = createMemo(() => !isEmpty(Object.values(filteredComponents()).flatMap((i) => i)));

  const { visibleItems, setVisibleItems } = useVisibleItems(props);

  return (
    <Show when={hasVisibleComponents()} fallback={!isNil(props.EmptyState) ? <props.EmptyState /> : undefined}>
      <ArkAccordion.Root
        class="w-full"
        value={visibleItems()}
        onValueChange={(details) => {
          setVisibleItems(details.value);
        }}
        collapsible
        multiple
        orientation="vertical"
      >
        <For each={props.items}>
          {(item) => {
            const components = createMemo(() => filteredComponents()[item.key]);
            return (
              <ArkAccordion.Item hidden={isEmpty(components())} value={item.key}>
                <ArkAccordion.ItemContext>
                  {(context) => (
                    <>
                      <div class="relative">
                        <ArkAccordion.ItemTrigger class="group flex w-full items-center gap-2 border-b border-subtle bg-surface-default p-2.5 select-none focus-visible:outline-default focus-visible:-outline-offset-1">
                          <ArkAccordion.ItemIndicator class="rotate-0 transition-transform duration-200 ease-linear open:rotate-90">
                            <Icon name="chevronRight" type="solid" size="md" class="icon-tertiary" />
                          </ArkAccordion.ItemIndicator>
                          <Text as="h6" class="text-base-semibold text-primary" text={item.label} truncate />
                        </ArkAccordion.ItemTrigger>
                        <Switch>
                          <Match when={item.button?.type === "destructive"}>
                            <div class="absolute top-1/2 right-2.5 -translate-y-1/2">
                              <IconButton
                                variant="tertiary"
                                icon="trash"
                                size="medium"
                                onClick={(item.button as AccordionItemDestructiveButton).onClick}
                                aria-label={(item.button as AccordionItemDestructiveButton)["aria-label"]}
                              />
                            </div>
                          </Match>
                          <Match when={item.button?.type === "dropdown"}>
                            <div class="absolute top-1/2 right-2.5 z-10 -translate-y-1/2">
                              <IconButtonMenu
                                items={(item.button as AccordionItemDropdownButton).items}
                                aria-label={(item.button as AccordionItemDropdownButton)["aria-label"]}
                              />
                            </div>
                          </Match>
                        </Switch>
                      </div>
                      <ArkAccordion.ItemContent class="grid grid-rows-[1fr] overflow-hidden rounded-b bg-surface-extra-subtle transition-all duration-250 ease-out closed:grid-rows-[0fr] closed:duration-200 closed:ease-in">
                        <div
                          class="flex min-w-0 flex-col"
                          inert={!context().expanded}
                          classList={{
                            "m-2 gap-1": props.variant === "compact",
                            "m-4 gap-4": props.variant === "default",
                            "": props.variant === "list",
                          }}
                        >
                          <For each={components()}>{({ Component }) => <Component />}</For>
                        </div>
                      </ArkAccordion.ItemContent>
                    </>
                  )}
                </ArkAccordion.ItemContext>
              </ArkAccordion.Item>
            );
          }}
        </For>
      </ArkAccordion.Root>
    </Show>
  );
};

const useVisibleItems = (props: Pick<AccordionProps, "items" | "initialClosed">) => {
  const [visible, setVisible] = createSignal(
    props.initialClosed === true ? [] : untrack(() => props.items.map((i) => i.key)),
  );

  const previousItems = createPreviousMemo(() => props.items);

  createEffect(() => {
    if (props.initialClosed === true) {
      return;
    }
    const newItems = difference(
      props.items.map((i) => i.key),
      previousItems()?.map((i) => i.key) ?? [],
    );
    setVisible((prev) => [...prev, ...newItems]);
  });
  return { visibleItems: visible, setVisibleItems: setVisible };
};

type FieldsAccordionItem = Omit<AccordionItem, "compact">;

export type FieldsAccordionProps = Omit<AccordionProps, "items" | "variant"> & {
  items: FieldsAccordionItem[];
};

export const FieldsAccordion: VoidComponent<FieldsAccordionProps> = (props) => (
  <Accordion
    variant="compact"
    initialClosed={props.initialClosed}
    items={props.items}
    filterQuery={props.filterQuery}
    EmptyState={props.EmptyState}
  />
);

export const PropertiesAccordion: VoidComponent<AccordionProps> = (props) => (
  <Accordion
    variant={props.variant}
    initialClosed={props.initialClosed}
    items={props.items}
    filterQuery={props.filterQuery}
    EmptyState={props.EmptyState}
  />
);
